import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Ban, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { useBooking } from '../contexts/BookingContext';
import { useAuth } from '../contexts/AuthContext';
import { toast } from '../hooks/use-toast';
import { generateTimeSlots, formatTime } from '../utils/timeSlots';

const TIME_SLOTS = generateTimeSlots(6, 23, 30);

// Helper functions
const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-IN', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'Asia/Kolkata'
  });
};

const SlotBlocking = () => {
  const { blockedSlots, fetchBlockedSlots, addBlockedSlot, removeBlockedSlot } = useBooking();
  const { user, isVenueManager, canEditBookings } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    date: '',
    startTime: '',
    endTime: '',
    reason: ''
  });

  // Load blocked slots on mount
  useEffect(() => {
    fetchBlockedSlots();
  }, [fetchBlockedSlots]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await addBlockedSlot({
        date: formData.date,
        start_time: formData.startTime,
        end_time: formData.endTime,
        reason: formData.reason
      });
      
      toast({
        title: "Slot Blocked",
        description: "The time slot has been blocked successfully.",
      });
      
      setFormData({ date: '', startTime: '', endTime: '', reason: '' });
      setShowForm(false);
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (id) => {
    if (window.confirm('Are you sure you want to unblock this slot?')) {
      try {
        await removeBlockedSlot(id);
        toast({
          title: "Slot Unblocked",
          description: "The time slot is now available for booking.",
        });
      } catch (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive"
        });
      }
    }
  };

  const sortedBlocks = [...blockedSlots].sort((a, b) => {
    if (a.date !== b.date) return new Date(a.date) - new Date(b.date);
    const startTimeA = a.start_time || a.startTime || '';
    const startTimeB = b.start_time || b.startTime || '';
    return startTimeA.localeCompare(startTimeB);
  });

  return (
    <div className="p-6 pt-14 lg:pt-6 pb-32 lg:pb-6 space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Blocked Slots</h1>
          <p className="text-gray-500">Manage ground availability</p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={() => fetchBlockedSlots()}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {canEditBookings && (
            <Button onClick={() => setShowForm(!showForm)}>
              <Plus className="h-4 w-4 mr-2" />
              Block Slot
            </Button>
          )}
        </div>
      </div>

      {/* Block Form - Only shown for users who can edit */}
      {showForm && canEditBookings && (
        <Card>
          <CardHeader>
            <CardTitle>Block Time Slot</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    type="date"
                    required
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    min={new Date().toISOString().split('T')[0]}
                  />
                </div>
                <div>
                  <Label htmlFor="reason">Reason</Label>
                  <Input
                    id="reason"
                    required
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    placeholder="e.g., Ground maintenance"
                  />
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="startTime">Start Time</Label>
                  <select
                    id="startTime"
                    required
                    className="w-full p-2 border rounded"
                    value={formData.startTime}
                    onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                  >
                    <option value="">Select start time</option>
                    {TIME_SLOTS.map((time) => (
                      <option key={time} value={time}>
                        {formatTime(time)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="endTime">End Time</Label>
                  <select
                    id="endTime"
                    required
                    className="w-full p-2 border rounded"
                    value={formData.endTime}
                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                  >
                    <option value="">Select end time</option>
                    {TIME_SLOTS.map((time) => (
                      <option key={time} value={time}>
                        {formatTime(time)}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex space-x-2">
                <Button type="submit" className="flex-1" disabled={loading}>
                  {loading ? 'Blocking...' : 'Block Slot'}
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)} className="flex-1">
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Blocked Slots List */}
      <Card>
        <CardHeader>
          <CardTitle>Active Blocks</CardTitle>
        </CardHeader>
        <CardContent>
          {sortedBlocks.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Ban className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No blocked slots</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedBlocks.map((block) => {
                const startTime = block.start_time || block.startTime || '';
                const endTime = block.end_time || block.endTime || '';
                const createdBy = block.created_by || block.createdBy || 'System';
                
                return (
                  <div
                    key={block.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-4">
                        <div>
                          <p className="font-semibold">{formatDate(block.date)}</p>
                          <p className="text-sm text-gray-500">
                            {formatTime(startTime)} - {formatTime(endTime)}
                          </p>
                        </div>
                        <div className="border-l pl-4">
                          <p className="text-sm font-medium">{block.reason}</p>
                          <p className="text-xs text-gray-500">By {createdBy}</p>
                        </div>
                      </div>
                    </div>
                    {canEditBookings && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRemove(block.id)}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SlotBlocking;
